import { c as _c } from "react/compiler-runtime";
import type { Premiere } from "@filmweb/sanity-types";
import { endOfMonth, format, isPast, isSameYear, isToday, parse } from "date-fns";
import { locale } from "src/lib/locale/date-fns";
;
import Button from "src/components/button/Button";
import WatchlistButton from "src/components/filmwebid/WatchlistButton";
import withWatchlistState from "src/components/filmwebid/withWatchlistState";
import CalendarSvg from "src/components/svg/CalendarSvg";
import { useProgramDatesContext, useSelectedCinemaContext } from "src/lib/contexts/ContextTypes";
import styles from './NoShows.module.css';
import classNames from 'classnames';
import { locale as loc } from "@locale";
import { formatLocaleString } from "src/lib/locale/helpers";
import getThemeStyles from "src/lib/styling/getThemeStyles";
import { defaultMovieIdentifierType } from "src/lib/filmwebid/useMovieIdentifier";

//#region [Props]
type NoShowsProps = {
  edi?: string | null;
  movieTitle: string;
  premiere?: Premiere | null;
  className?: string;
  /** the film has shows, but not at the selected cinemas */
  isFilteredByCinema?: boolean;
};
//#endregion

//#region [Component]
export default function NoShows(t0) {
  const $ = _c(30);
  const {
    edi,
    movieTitle,
    premiere,
    className,
    isFilteredByCinema: t1
  } = t0;
  const isFilteredByCinema = t1 === undefined ? false : t1;
  const datesContext = useProgramDatesContext();
  const cinemaContext = useSelectedCinemaContext();
  let desktopTextToShow = loc.program.noShows.noMoreShowsDefault;
  let mobileTextToShow;
  if ($[0] !== cinemaContext || $[1] !== datesContext || $[2] !== movieTitle || $[3] !== premiere) {
    let premiereParsed;
    if (premiere?.premiereDate || premiere?.reReleaseDate) {
      premiereParsed = parse(premiere.reReleaseDate ?? premiere.premiereDate, "yyyy-MM-dd", new Date());
    } else {
      if (premiere?.expectedPremiereDate) {
        premiereParsed = parse(premiere.expectedPremiereDate, "yyyy-MM-dd", new Date());
      }
    }
    const isExpectedPremiere = !!premiere?.expectedPremiereDate;
    const isPremiereUpcoming = !!premiereParsed && (isExpectedPremiere ? !isPast(endOfMonth(premiereParsed)) : !isPast(premiereParsed) || isToday(premiereParsed));
    const nextShowDate = datesContext.availableDates?.find(_temp);
    if (isExpectedPremiere && isPremiereUpcoming) {
      const isThisYear = isSameYear(premiereParsed, new Date());
      desktopTextToShow = formatLocaleString(loc.program.noShows.expectedPremiere, {
        movieTitle,
        premiere: format(premiereParsed, isThisYear ? "MMMM" : "MMMM yyyy", {
          locale
        })
      });
      mobileTextToShow = <><strong>{formatLocaleString(loc.program.noShows.expectedPremiereMobile1, {
            premiere: format(premiereParsed, isThisYear ? "MMMM" : "MMMM yyyy", {
              locale
            })
          })}</strong>{loc.program.noShows.expectedPremiereMobile2}</>;
    } else {
      if (nextShowDate) {
        let t2;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <CalendarSvg className={styles.SCalendarSvg} />;
          $[6] = t2;
        } else {
          t2 = $[6];
        }
        const t3 = <Button className={styles.SInlineDateSelector} text={format(new Date(nextShowDate), "iiii d. MMMM", {
          locale
        })} onClick={() => {
          datesContext.setSelectedDate(nextShowDate);
          cinemaContext.setSelectedCinemas(null);
        }} icon={t2} inverse={true} />;
        let t4;
        if ($[7] !== t3) {
          t4 = <>{loc.program.noShows.nextShow} {t3}</>;
          $[7] = t3;
          $[8] = t4;
        } else {
          t4 = $[8];
        }
        desktopTextToShow = t4;
      } else {
        if (!isExpectedPremiere && isPremiereUpcoming) {
          const isThisYear_0 = isSameYear(premiereParsed, new Date());
          desktopTextToShow = formatLocaleString(loc.program.noShows.hasPremiere, {
            movieTitle,
            premiere: format(premiereParsed, isThisYear_0 ? "d. MMMM" : "d. MMMM yyyy", {
              locale
            })
          });
          mobileTextToShow = <><strong>{formatLocaleString(loc.program.noShows.hasPremiereMobile1, {
                premiere: format(premiereParsed, isThisYear_0 ? "d. MMMM" : "d. MMMM yyyy", {
                  locale
                })
              })}</strong>{loc.program.noShows.hasPremiereMobile2}</>;
        } else {
          if (!isExpectedPremiere && premiereParsed) {
            desktopTextToShow = formatLocaleString(loc.program.noShows.premierePassed, {
              movieTitle,
              premiere: format(premiereParsed, "d. MMMM yyyy", {
                locale
              })
            });
            const t2 = <strong>{formatLocaleString(loc.program.noShows.premierePassedMobile1, {
                premiere: format(premiereParsed, "d. MMMM yyyy", {
                  locale
                })
              })}</strong>;
            let t3;
            if ($[9] !== t2) {
              t3 = <>{t2}{loc.program.noShows.premierePassedMobile2}</>;
              $[9] = t2;
              $[10] = t3;
            } else {
              t3 = $[10];
            }
            mobileTextToShow = t3;
          }
        }
      }
    }
    $[0] = cinemaContext;
    $[1] = datesContext;
    $[2] = movieTitle;
    $[3] = premiere;
    $[4] = desktopTextToShow;
    $[5] = mobileTextToShow;
  } else {
    desktopTextToShow = $[4];
    mobileTextToShow = $[5];
  }
  let t2;
  let t3;
  if ($[11] !== cinemaContext || $[12] !== className || $[13] !== isFilteredByCinema) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const themeStyles = getThemeStyles(styles);
      if (isFilteredByCinema) {
        let t4;
        if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <div>{loc.program.noShows.noShowsForSelectedCinemas}</div>;
          $[16] = t4;
        } else {
          t4 = $[16];
        }
        let t5;
        if ($[17] !== cinemaContext) {
          t5 = <Button text={loc.program.noShows.showAllCinemas} onClick={() => cinemaContext.setSelectedCinemas(null)} inverse={true} />;
          $[17] = cinemaContext;
          $[18] = t5;
        } else {
          t5 = $[18];
        }
        t3 = <div className={classNames(styles.SNoShows, className, themeStyles)}>{t4}{t5}</div>;
        break bb0;
      }
      t2 = classNames(styles.SNoShows, className, themeStyles);
    }
    $[11] = cinemaContext;
    $[12] = className;
    $[13] = isFilteredByCinema;
    $[14] = t2;
    $[15] = t3;
  } else {
    t2 = $[14];
    t3 = $[15];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[19] !== edi) {
    t4 = !!edi && <WLWatchListButton movieIdentifier={{
      type: defaultMovieIdentifierType,
      value: edi
    }} className={styles.SWatchlistIcon} />;
    $[19] = edi;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const t5 = mobileTextToShow ? mobileTextToShow : desktopTextToShow;
  let t6;
  if ($[21] !== t5) {
    t6 = <div className={styles.SMobileText}>{t5}</div>;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== desktopTextToShow) {
    t7 = <div className={styles.SDesktopText}>{desktopTextToShow}</div>;
    $[23] = desktopTextToShow;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t2 || $[26] !== t4 || $[27] !== t6 || $[28] !== t7) {
    t8 = <div className={t2}>{t4}{t6}{t7}</div>;
    $[25] = t2;
    $[26] = t4;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
//#endregion
function _temp(date) {
  return !isToday(date);
}
const WLWatchListButton = withWatchlistState(WatchlistButton);